import React, { useState, useMemo } from 'react'
import { Typography, Paper, Chip, Button, Collapse } from '@material-ui/core'
import { ExpandMore, ExpandLess } from '@material-ui/icons'
import InlineTemplateCreator from './InlineTemplateCreator'

const COST_TYPE_STYLES = {
  labor: { bg: '#e8f5e9', color: '#2e7d32', label: 'L' },
  equipment: { bg: '#fff3e0', color: '#e65100', label: 'E' },
  material: { bg: '#e8eaf6', color: '#283593', label: 'M' },
  subcontract: { bg: '#e3f2fd', color: '#1565c0', label: 'S' },
  disposal: { bg: '#fce4ec', color: '#c62828', label: 'D' }
}

const ServiceCard = ({ service, template, url, onTemplateCreated }) => {
  const [expanded, setExpanded] = useState(false)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)

  const hasTemplate = !!template
  const components = template ? template.components : []

  const itemSummary = (service.items || [])
    .map(i => `#${i.itemNo}`)
    .join(', ')

  return (
    <Paper variant='outlined' style={{
      marginBottom: 12,
      borderColor: !hasTemplate ? '#ffcc80' : '#e0e0e0',
      backgroundColor: !hasTemplate ? '#fff8e1' : '#fff'
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '10px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: expanded ? '#f5f5f5' : 'transparent'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {expanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
          <Chip label={service.serviceCategory} size='small'
            style={{ fontSize: 10, fontWeight: 600 }} />
          <Typography variant='body2' style={{ fontWeight: 600 }}>
            {service.serviceName}
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            ← {itemSummary} ({service.totalQty} {service.unit || ''})
          </Typography>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasTemplate && (
            <div style={{ display: 'flex', gap: 3 }}>
              {components.map((c, i) => {
                const style = COST_TYPE_STYLES[c.costType] || {}
                return (
                  <span key={i} style={{
                    backgroundColor: style.bg, color: style.color,
                    padding: '1px 4px', borderRadius: 2, fontSize: 9, fontWeight: 600
                  }}>
                    {style.label}
                  </span>
                )
              })}
            </div>
          )}
          {!hasTemplate && (
            <Typography variant='caption' style={{ color: '#e65100', fontStyle: 'italic' }}>
              Needs template
            </Typography>
          )}
        </div>
      </div>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #eee' }}>
          {!hasTemplate ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Typography variant='body2' color='textSecondary' style={{ marginBottom: 12 }}>
                No template exists for this service. Create one to define its cost components.
              </Typography>
              <Button variant='contained' color='primary' size='small'
                onClick={(e) => { e.stopPropagation(); setTemplateDialogOpen(true) }}>
                Create Template
              </Button>
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: '#888', fontSize: 11, textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>Type</th>
                    <th style={{ padding: '6px 8px' }}>Description</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '6px 8px' }}>Unit</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rate/Cost</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {components.map((c, i) => {
                    const style = COST_TYPE_STYLES[c.costType] || {}
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{
                            backgroundColor: style.bg, color: style.color,
                            padding: '1px 6px', borderRadius: 3, fontSize: 10
                          }}>
                            {c.costType}
                          </span>
                        </td>
                        <td style={{ padding: '6px 8px' }}>{c.description || '(click to set)'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{service.totalQty}</td>
                        <td style={{ padding: '6px 8px' }}>{service.unit || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#999' }}>—</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#999' }}>$0.00</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <Typography variant='caption' color='textSecondary' style={{ display: 'block', marginTop: 8 }}>
                Rate linking and cost calculation coming next. Components show template defaults for now.
              </Typography>
            </>
          )}
        </div>
      </Collapse>

      <InlineTemplateCreator
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        service={service}
        url={url}
        onCreated={onTemplateCreated}
      />
    </Paper>
  )
}

const ServiceEstimator = ({ aggregatedServices, templates, url, onTemplateCreated }) => {
  const templateMap = useMemo(() => {
    const map = {}
    for (const t of (templates || [])) {
      map[t.serviceId] = t
    }
    return map
  }, [templates])

  if (!aggregatedServices || aggregatedServices.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <Typography variant='body2' color='textSecondary'>
          No services assigned yet. Tag bid items above to start estimating.
        </Typography>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <Typography variant='subtitle2' style={{ fontWeight: 700 }}>Services to Estimate</Typography>
          <Typography variant='caption' color='textSecondary'>
            Expand to see components. {aggregatedServices.length} service{aggregatedServices.length !== 1 ? 's' : ''} assigned.
          </Typography>
        </div>
      </div>

      {aggregatedServices.map(svc => (
        <ServiceCard
          key={svc.serviceId}
          service={svc}
          template={templateMap[svc.serviceId]}
          url={url}
          onTemplateCreated={onTemplateCreated}
        />
      ))}
    </div>
  )
}

export default ServiceEstimator
